import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, DeleteOutlined, EditOutlined, LinkOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  deleteMeituanShop,
  getMeituanShopAuthUrl,
  listMeituanShops,
  refreshMeituanShopToken,
  updateMeituanShop,
  type MeituanShop,
  type SaveMeituanShopRequest,
} from '../services/meituanShopService';

const { Title } = Typography;
const { Text } = Typography;

const defaultFormValues: SaveMeituanShopRequest = {
  shopId: '',
  shopName: '',
  opBizCode: '',
  opBizName: '',
  scope: 'all',
  status: 1,
  autoAcceptOrder: 1,
  autoConfirmChange: 1,
  sort: 0,
  remark: '',
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const scopeNameMap: Record<string, string> = {
  tuangou: '团购',
  ugc: '评价',
  yuding: '预订',
  merchantdata: '经营数据',
  customercenter: '客资中心',
  manager: '核销管理',
  poiqrcode: '门店二维码',
  technician: '手艺人',
  thirdresource: '外部厂商信息',
  selfconsume: '自助核销',
  registration: '医院挂号',
  shangpin: '商品管理',
  dingdan: '订单信息',
  generalreserve: '通用预约',
  member: '商家会员',
  merchantreceipt: '商家券',
  finance: '财务',
  dzminiprogram: '小程序对接',
  clubactivity: '俱乐部拼场活动',
  tijianzixun: '体检咨询',
};

const formatScope = (value?: string) => {
  if (!value) return '全部权限';
  const scopes = value.split(',').map(item => item.trim()).filter(Boolean);
  if (!scopes.length || scopes.includes('all')) return '全部权限';
  return scopes.map(item => scopeNameMap[item] || item).join('、');
};

const MeituanShopManagement: React.FC = () => {
  const [form] = Form.useForm<SaveMeituanShopRequest>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shops, setShops] = useState<MeituanShop[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<string>();
  const [modalVisible, setModalVisible] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [authBusinessId, setAuthBusinessId] = useState('58');
  const [editingShop, setEditingShop] = useState<MeituanShop | null>(null);
  const [authorizing, setAuthorizing] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string>();
  const [updatingAutoAcceptId, setUpdatingAutoAcceptId] = useState<string>();
  const [updatingAutoConfirmChangeId, setUpdatingAutoConfirmChangeId] = useState<string>();

  const loadShops = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanShops({
        page: nextPage,
        pageSize: nextPageSize,
        keyword: keyword || undefined,
        status,
      });
      setShops(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载美团门店失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadShops();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadShops(1, pageSize);
  };

  const handleReset = () => {
    setKeyword('');
    setStatus(undefined);
    setPage(1);
    setTimeout(() => {
      void loadShops(1, pageSize);
    }, 0);
  };

  const openEditModal = (record: MeituanShop) => {
    setEditingShop(record);
    form.setFieldsValue({
      shopId: record.shopId,
      shopName: record.shopName,
      opBizCode: record.opBizCode || '',
      opBizName: record.opBizName || '',
      scope: record.scope || 'all',
      status: record.status,
      autoAcceptOrder: record.autoAcceptOrder ?? 1,
      autoConfirmChange: record.autoConfirmChange ?? 1,
      sort: record.sort,
      remark: record.remark,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (!editingShop) {
        message.error('门店只能通过美团授权回调新增');
        return;
      }
      await updateMeituanShop(editingShop.id, { ...defaultFormValues, ...values });
      message.success('更新成功');
      setModalVisible(false);
      void loadShops();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMeituanShop(id);
      message.success('删除成功');
      void loadShops();
    } catch {
      message.error('删除失败');
    }
  };

  const handleRefreshToken = async (record: MeituanShop) => {
    setRefreshingId(record.id);
    try {
      await refreshMeituanShopToken(record.id, record.businessId || record.opBizCode);
      message.success('刷新Token成功');
      void loadShops();
    } catch {
      message.error('刷新Token失败');
    } finally {
      setRefreshingId(undefined);
    }
  };

  const buildSavePayload = (record: MeituanShop, overrides: Partial<SaveMeituanShopRequest> = {}): SaveMeituanShopRequest => ({
    shopId: record.shopId,
    shopName: record.shopName,
    businessId: record.businessId,
    state: record.state,
    opBizCode: record.opBizCode || '',
    opBizName: record.opBizName || '',
    authCode: record.authCode,
    scope: record.scope || 'all',
    status: record.status,
    autoAcceptOrder: record.autoAcceptOrder ?? 1,
    autoConfirmChange: record.autoConfirmChange ?? 1,
    sort: record.sort,
    remark: record.remark || '',
    ...overrides,
  });

  const handleToggleAutoAccept = async (record: MeituanShop, checked: boolean) => {
    setUpdatingAutoAcceptId(record.id);
    try {
      await updateMeituanShop(record.id, buildSavePayload(record, { autoAcceptOrder: checked ? 1 : 0 }));
      message.success(checked ? '已开启自动接单' : '已关闭自动接单');
      setShops(prev => prev.map(item => item.id === record.id ? { ...item, autoAcceptOrder: checked ? 1 : 0 } : item));
    } catch {
      message.error('更新自动接单配置失败');
    } finally {
      setUpdatingAutoAcceptId(undefined);
    }
  };

  const handleToggleAutoConfirmChange = async (record: MeituanShop, checked: boolean) => {
    setUpdatingAutoConfirmChangeId(record.id);
    try {
      await updateMeituanShop(record.id, buildSavePayload(record, { autoConfirmChange: checked ? 1 : 0 }));
      message.success(checked ? '已开启改约自动确认' : '已关闭改约自动确认');
      setShops(prev => prev.map(item => item.id === record.id ? { ...item, autoConfirmChange: checked ? 1 : 0 } : item));
    } catch {
      message.error('更新改约自动确认配置失败');
    } finally {
      setUpdatingAutoConfirmChangeId(undefined);
    }
  };

  const handleOpenAuthModal = () => {
    setAuthBusinessId('58');
    setAuthModalVisible(true);
  };

  const handleCopyText = async (value?: string) => {
    const text = (value || '').trim();
    if (!text) {
      message.warning('暂无可复制内容');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  const handleOpenAuth = async () => {
    setAuthorizing(true);
    try {
      const res = await getMeituanShopAuthUrl(authBusinessId);
      const authUrl = (res.data.authUrl || '').trim();
      if (!authUrl) {
        message.warning('获取美团授权地址失败');
        return;
      }
      window.open(authUrl, '_blank', 'noopener,noreferrer');
      setAuthModalVisible(false);
    } catch {
      message.error('生成美团授权地址失败，请检查美团配置');
    } finally {
      setAuthorizing(false);
    }
  };

  const columns: ColumnsType<MeituanShop> = [
    { title: '授权实体名称', dataIndex: 'shopName', key: 'shopName', width: 200, ellipsis: true },
    { title: '授权实体ID', dataIndex: 'shopId', key: 'shopId', width: 240, ellipsis: true },
    {
      title: '业务编码',
      dataIndex: 'opBizCode',
      key: 'opBizCode',
      width: 360,
      render: value => {
        const text = (value || '').trim();
        if (!text) return '-';
        return (
          <Space size={6} align="start">
            <Tooltip title={text}>
              <Text style={{ maxWidth: 300 }} copyable={false}>
                {text}
              </Text>
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyText(text)}
            />
          </Space>
        );
      },
    },
    { title: '业务名称', dataIndex: 'opBizName', key: 'opBizName', width: 170, ellipsis: true, render: value => value || '-' },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      width: 260,
      render: value => (
        <Tooltip title={value || 'all'}>
          <Text ellipsis style={{ maxWidth: 240 }}>
            {formatScope(value)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '授权状态',
      key: 'authStatus',
      width: 110,
      render: (_, record) => (
        <Tag color={record.accessToken ? 'green' : 'orange'}>
          {record.accessToken ? '已授权' : '未授权'}
        </Tag>
      ),
    },
    { title: '授权时间', dataIndex: 'authorizedAt', key: 'authorizedAt', width: 180, render: value => formatDateTime(value) },
    { title: 'Token过期时间', dataIndex: 'tokenExpiresAt', key: 'tokenExpiresAt', width: 180, render: value => formatDateTime(value) },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: value => <Tag color={value === 1 ? 'green' : 'red'}>{value === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '自动接单',
      dataIndex: 'autoAcceptOrder',
      key: 'autoAcceptOrder',
      width: 110,
      render: (value, record) => (
        <Switch
          size="small"
          checked={value !== 0}
          checkedChildren="开"
          unCheckedChildren="关"
          loading={updatingAutoAcceptId === record.id}
          onChange={checked => handleToggleAutoAccept(record, checked)}
        />
      ),
    },
    {
      title: '改约自动确认',
      dataIndex: 'autoConfirmChange',
      key: 'autoConfirmChange',
      width: 140,
      render: (value, record) => (
        <Switch
          size="small"
          checked={value !== 0}
          checkedChildren="开"
          unCheckedChildren="关"
          loading={updatingAutoConfirmChangeId === record.id}
          onChange={checked => handleToggleAutoConfirmChange(record, checked)}
        />
      ),
    },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            loading={refreshingId === record.id}
            disabled={!record.refreshToken}
            onClick={() => handleRefreshToken(record)}
          >
            刷新Token
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该美团门店吗？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>美团门店管理</Title>
      </div>

      <div className="page-content">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="门店授权说明"
          description="门店新增由美团后台配置的回调地址触发，授权成功后回调接口写入门店数据。当前页面只负责查看授权结果，并维护门店基础信息、启用状态和备注。"
        />

        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              allowClear
              style={{ width: 280 }}
            placeholder="搜索授权实体、业务编码、scope"
              value={keyword}
              onChange={event => setKeyword(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="状态"
              value={status}
              onChange={value => setStatus(value)}
              options={[
                { label: '启用', value: '1' },
                { label: '禁用', value: '0' },
              ]}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button type="primary" icon={<LinkOutlined />} onClick={handleOpenAuthModal}>
              授权门店
            </Button>
          </Space>
        </Card>

        <Card>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={shops}
            scroll={{ x: 1840 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: value => `共 ${value} 条`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPage);
                setPageSize(nextPageSize || 10);
              },
            }}
          />
        </Card>
      </div>

      <Modal
        title="选择授权类型"
        open={authModalVisible}
        onCancel={() => setAuthModalVisible(false)}
        onOk={handleOpenAuth}
        confirmLoading={authorizing}
        okText="确定授权"
        cancelText="取消"
        destroyOnClose
      >
        <Select
          style={{ width: '100%' }}
          size="large"
          value={authBusinessId}
          onChange={value => setAuthBusinessId(value)}
          options={[
            { label: '服务零售授权', value: '58' },
            { label: '服务零售(客户)授权', value: '59' },
          ]}
        />
      </Modal>

      <Modal
        title="编辑美团门店"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        width={760}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={defaultFormValues}>
          <Form.Item name="shopName" label="授权实体名称" rules={[{ required: true, message: '请输入授权实体名称' }]}>
            <Input placeholder="请输入授权实体名称" />
          </Form.Item>
          <Form.Item name="shopId" label="授权实体ID" rules={[{ required: true, message: '请输入授权实体ID' }]}>
            <Input placeholder="请输入授权实体ID" />
          </Form.Item>
          <Form.Item name="opBizCode" label="业务编码">
            <Input placeholder="请输入 op_biz_code" />
          </Form.Item>
          <Form.Item name="opBizName" label="业务名称">
            <Input placeholder="请输入 op_biz_name" />
          </Form.Item>
          <Form.Item name="scope" label="Scope">
            <Input placeholder="请输入 scope" />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
            <Select
              options={[
                { label: '启用', value: 1 },
                { label: '禁用', value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="autoAcceptOrder"
            label="自动接单"
            valuePropName="checked"
            getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
            getValueProps={(value?: number) => ({ checked: value !== 0 })}
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item
            name="autoConfirmChange"
            label="改约自动确认"
            valuePropName="checked"
            getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
            getValueProps={(value?: number) => ({ checked: value !== 0 })}
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MeituanShopManagement;
